/**
 * The plan as a month calendar: Monday-to-Friday weeks, each day's kind, and the unit taught in
 * each week. Pure: `today` is a parameter, and dates stay `YYYY-MM-DD` strings (KTD10).
 */
import { addDays, weekDates, weekOf } from './calendar';
import type { ScheduledUnit } from './schedule';
import type { Calendar, CalendarBreak, IsoDate } from './types';

/** A calendar month; `month` runs 1 to 12. */
export interface YearMonth {
  year: number;
  month: number;
}

export interface MonthDay {
  date: IsoDate;
  dayOfMonth: number;
  /** False for the days of a row's week that belong to the month before or after. */
  inMonth: boolean;
  /** Weekends are never shown, so a weekday is a school day, a break day, or outside the year. */
  kind: 'school' | 'break' | 'outside_year';
  breakName?: string;
  breakKind?: CalendarBreak['kind'];
  isToday: boolean;
  /** Set on the combined calendar only: every plan teaching that day, in source order (R9). */
  teaching?: DayTeaching[];
}

/**
 * One plan teaching on a combined-calendar day: a school day in its own calendar in a week its unit
 * runs, or any weekday of a week past its last day (R9, R10). Drawn as a dot in its colour (KTD6).
 */
export interface DayTeaching {
  planId: string;
  colorIndex: number;
  label: string;
  unitId: string;
  unitName: string;
  done: boolean;
  pastLastDay: boolean;
}

export interface MonthUnit {
  id: string;
  name: string;
  /** The unit's position in the plan, which picks its colour. */
  index: number;
  done: boolean;
  pastLastDay: boolean;
  /** Set on the combined calendar only: the unit's plan, its colour, and its short label (KTD6). */
  planId?: string;
  colorIndex?: number;
  label?: string;
}

/** One plan on the combined calendar: its own calendar and its own scheduled units (R9). */
export interface MonthSource {
  planId: string;
  /** The plan's creation order, which picks its colour (KTD6). */
  colorIndex: number;
  label: string;
  calendar: Calendar;
  rows: readonly ScheduledUnit[];
}

export interface MonthWeek {
  monday: IsoDate;
  days: MonthDay[];
  /** The units whose scheduled weeks include this one; the schedule never puts two in a week. */
  units: MonthUnit[];
  /** The columns (0 Monday … 4 Friday) a unit bar covers: the week's school days, or all five past the last day. */
  barStart: number;
  barEnd: number;
}

/** Months walked at most: a two-year calendar plus a long overrun (see MAX_TEACHING_WEEKS). */
const MAX_MONTHS = 40;

/** The month a date falls in. */
export const monthOf = (date: IsoDate): YearMonth => ({ year: Number(date.slice(0, 4)), month: Number(date.slice(5, 7)) });
const ordinal = ({ year, month }: YearMonth) => year * 12 + (month - 1);
const fromOrdinal = (n: number): YearMonth => ({ year: Math.floor(n / 12), month: (n % 12) + 1 });
/** The month's first day. */
export const firstOf = ({ year, month }: YearMonth): IsoDate => `${year}-${String(month).padStart(2, '0')}-01`;
/** The month after. */
export const nextMonth = (m: YearMonth): YearMonth => fromOrdinal(ordinal(m) + 1);
export const sameMonth = (a: YearMonth, b: YearMonth) => ordinal(a) === ordinal(b);
const inRange = (date: IsoDate, start: IsoDate, end: IsoDate) => start <= date && date <= end;

/** The months from `first` to `end`, capped at MAX_MONTHS. */
function monthsBetween(first: IsoDate, end: IsoDate): YearMonth[] {
  const from = ordinal(monthOf(first));
  const to = Math.min(ordinal(monthOf(end)), from + MAX_MONTHS - 1);
  return Array.from({ length: Math.max(0, to - from + 1) }, (_, i) => fromOrdinal(from + i));
}

/** The later of the last day and the Friday of the last week a unit runs into. */
function lastShown(calendar: Calendar, scheduled: readonly ScheduledUnit[]): IsoDate {
  const lastWeek = scheduled.flatMap(u => u.weekStarts).reduce<IsoDate | null>((max, w) => (max === null || w > max ? w : max), null);
  const lastFriday = lastWeek ? addDays(lastWeek, 4) : null;
  return lastFriday && lastFriday > calendar.lastDay ? lastFriday : calendar.lastDay;
}

/** The months from the first day to the last, run on through any month a unit overruns into. */
export function schoolMonths(calendar: Calendar, scheduled: readonly ScheduledUnit[] = []): YearMonth[] {
  return monthsBetween(calendar.firstDay, lastShown(calendar, scheduled));
}

/** The months from the earliest first day to the latest last day or overrun of any plan. */
export function combinedMonths(sources: readonly MonthSource[]): YearMonth[] {
  if (sources.length === 0) return [];
  const first = sources.map(s => s.calendar.firstDay).reduce((a, b) => (b < a ? b : a));
  const end = sources.map(s => lastShown(s.calendar, s.rows)).reduce((a, b) => (b > a ? b : a));
  return monthsBetween(first, end);
}

export type DayKind = Pick<MonthDay, 'kind' | 'breakName' | 'breakKind'>;

/** A weekday's kind: outside the year, in a break, or a school day. */
export function dayKind(calendar: Calendar, date: IsoDate): DayKind {
  if (!inRange(date, calendar.firstDay, calendar.lastDay)) return { kind: 'outside_year' };
  const b = calendar.breaks.find(x => inRange(date, x.start, x.end));
  return b ? { kind: 'break', breakName: b.name, breakKind: b.kind } : { kind: 'school' };
}

/** School if any plan teaches that day; else the first plan's break; outside the year only in every plan (R10). */
function combinedDayKind(sources: readonly MonthSource[], date: IsoDate): DayKind {
  const kinds = sources.map(s => dayKind(s.calendar, date));
  return kinds.find(k => k.kind === 'school') ?? kinds.find(k => k.kind === 'break') ?? { kind: 'outside_year' };
}

/** The Mondays of the Monday-to-Friday weeks with at least one weekday in the month. */
export function monthMondays({ year, month }: YearMonth): IsoDate[] {
  const next = firstOf(nextMonth({ year, month }));
  const mondays: IsoDate[] = [];
  // `weekOf` moves a month that starts on a weekend to its first Monday.
  for (let monday = weekOf(firstOf({ year, month })); monday < next; monday = addDays(monday, 7)) mondays.push(monday);
  return mondays;
}

/** The Monday-to-Friday weeks with at least one weekday in the month, each day's kind, and each week's units. */
function grid(year: number, month: number, today: IsoDate, kindOf: (date: IsoDate) => DayKind, unitsIn: (monday: IsoDate) => MonthUnit[],
  teachingOn?: (monday: IsoDate, date: IsoDate) => DayTeaching[]): MonthWeek[] {
  const first = firstOf({ year, month });
  const next = firstOf(nextMonth({ year, month }));
  return monthMondays({ year, month }).map(monday => {
    const days = weekDates(monday).map((date): MonthDay => {
      const d: MonthDay = { date, dayOfMonth: Number(date.slice(8, 10)), inMonth: date >= first && date < next, ...kindOf(date), isToday: date === today };
      return teachingOn ? { ...d, teaching: teachingOn(monday, date) } : d;
    });
    const school = days.flatMap((d, i) => (d.kind === 'school' ? [i] : []));
    return { monday, days, units: unitsIn(monday), barStart: school[0] ?? 0, barEnd: school.at(-1) ?? 4 };
  });
}

const unitsOn = (scheduled: readonly ScheduledUnit[], monday: IsoDate): MonthUnit[] =>
  scheduled.flatMap((u, index) => (u.weekStarts.includes(monday) ? [{ id: u.id, name: u.name, index, done: u.done, pastLastDay: u.pastLastDay }] : []));

/** The Monday-to-Friday weeks with at least one weekday in the month, and each week's unit. */
export function monthGrid(calendar: Calendar, scheduled: readonly ScheduledUnit[], year: number, month: number, today: IsoDate): MonthWeek[] {
  return grid(year, month, today, date => dayKind(calendar, date), monday => unitsOn(scheduled, monday));
}

/** The plans teaching on a day: its unit that week, on that plan's school days, or every weekday of a week past its last day. */
const teachingOn = (sources: readonly MonthSource[], monday: IsoDate, date: IsoDate): DayTeaching[] =>
  sources.flatMap(s => (monday > s.calendar.lastDay || dayKind(s.calendar, date).kind === 'school'
    ? unitsOn(s.rows, monday).map(u => ({ planId: s.planId, colorIndex: s.colorIndex, label: s.label, unitId: u.id, unitName: u.name, done: u.done, pastLastDay: u.pastLastDay }))
    : []));

/**
 * Every plan's month on one grid (R9, R10): each week carries every plan's unit for that Monday,
 * and each day every plan teaching it, placed by that plan's own calendar, in source order.
 */
export function combinedMonthGrid(sources: readonly MonthSource[], year: number, month: number, today: IsoDate): MonthWeek[] {
  return grid(year, month, today, date => combinedDayKind(sources, date), monday =>
    sources.flatMap(s => unitsOn(s.rows, monday).map(u => ({ ...u, planId: s.planId, colorIndex: s.colorIndex, label: s.label }))),
  (monday, date) => teachingOn(sources, monday, date));
}

/** Today's month, else the first month when today is before the range, else the last after it. */
export function defaultMonth(months: readonly YearMonth[], today: IsoDate): YearMonth {
  const now = ordinal(monthOf(today));
  if (months.length === 0) return monthOf(today);
  if (now < ordinal(months[0]!)) return months[0]!;
  if (now > ordinal(months.at(-1)!)) return months.at(-1)!;
  return monthOf(today);
}

/** The month to open on: today's, else the first before the year, else the last after it. */
export function initialMonth(calendar: Calendar, today: IsoDate, scheduled: readonly ScheduledUnit[] = []): YearMonth {
  return defaultMonth(schoolMonths(calendar, scheduled), today);
}

/** The month the combined calendar opens on, by the same rule across every plan's months. */
export function combinedInitialMonth(sources: readonly MonthSource[], today: IsoDate): YearMonth {
  return defaultMonth(combinedMonths(sources), today);
}

/** Previous or Next month (R9): `step` months on, or null past either end of `months` (none bounds nothing). */
export function stepMonth(month: YearMonth, step: number, months: readonly YearMonth[]): YearMonth | null {
  const target = fromOrdinal(ordinal(month) + step);
  if (months.length === 0) return target;
  return ordinal(months[0]!) <= ordinal(target) && ordinal(target) <= ordinal(months.at(-1)!) ? target : null;
}

/** Previous or Next week (R9): the Monday `step` weeks on, or null when that week has no day in `months`. */
export function stepWeek(monday: IsoDate, step: number, months: readonly YearMonth[]): IsoDate | null {
  const target = addDays(monday, 7 * step);
  if (months.length === 0) return target;
  return firstOf(months[0]!) <= addDays(target, 4) && target < firstOf(nextMonth(months.at(-1)!)) ? target : null;
}
